import { AlertDangerIcon, ButtonIcon, css, Feedback, styled, Tooltip, TrashIcon } from 'canoe';
import { Field, FieldArray, FieldAttributes, Form, Formik } from 'formik';
import { nanoid } from 'nanoid';
import React, { useCallback, useMemo, useRef } from 'react';
import { s3Image } from 'site-constants';
import * as Yup from 'yup';

import { DraggableValueCard, ValueGroup } from '@candu-ai/craft/ui';
import { FormSelectInputOption } from '@candu-ai/forms/document-types';

import { AddOptionIcon, AddOptionLabel, AddOptionWrapper } from './AddOption';
import { FieldInput, FieldLabel, FieldRow, FieldsContainer } from './OptionFields';

const ErrorMessage = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    margin-top: ${theme.spaces.xxs};
  `}
`;

const validationSchema = Yup.object().shape({
  inputOptions: Yup.array().of(
    Yup.object().shape({
      value: Yup.string(),
      label: Yup.string().required(),
    }),
  ),
});

interface InputOptionsProps {
  inputOptions?: FormSelectInputOption[];
  setProps: (props: Record<string, any>) => void;
  newOptionName?: string;
}

export const InputOptions = React.memo(
  ({ inputOptions, setProps, newOptionName = 'Option' }: InputOptionsProps) => {
    const emptyIsUndefined = (value: string | undefined) => (value !== '' ? value : undefined);
    const textInputRef = useRef<HTMLInputElement>(null);
    const valueInputRef = useRef<HTMLInputElement>(null);

    const handleSubmit = useCallback(
      (values: any) => {
        setProps({
          options: values.inputOptions.map((option: FormSelectInputOption) => ({
            label: emptyIsUndefined(option.label),
            value: emptyIsUndefined(option.value),
          })),
        });
      },
      [setProps],
    );

    const initialValues = useMemo(
      () => ({
        inputOptions: inputOptions?.map((option, index) => ({
          value: option.value,
          label: option.label,
          key: `${option.label}-${index}`,
        })),
      }),
      [inputOptions],
    );

    const DraggableOptions = useMemo(
      () => (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ submitForm, values, errors }) => {
            return (
              <FieldArray name="inputOptions">
                {({ remove, push, move }) => (
                  <Form>
                    {!values.inputOptions && <span>Empty</span>}
                    {values.inputOptions &&
                      values.inputOptions.map((option: FormSelectInputOption, index: number) => (
                        <DraggableValueCard
                          key={option.key}
                          padding="xxs"
                          index={index}
                          onDrop={(dragIndex, hoverIndex) => {
                            move(dragIndex, hoverIndex);
                            submitForm();
                          }}
                          placeholderImagePath={s3Image(
                            '/svg/toolbox-drag-placeholder-input-options.svg',
                          )}
                        >
                          <FieldsContainer>
                            <FieldRow>
                              <FieldLabel
                                htmlFor={`inputOption-label-${option.key}`}
                                element="label"
                              >
                                Text
                              </FieldLabel>
                              <Field name={`inputOptions[${index}].label`}>
                                {({ field, meta }: FieldAttributes<any>) => (
                                  <FieldInput
                                    {...field}
                                    ref={textInputRef}
                                    type="text"
                                    id={`inputOption-label-${option.key}`}
                                    value={option.label}
                                    onBlur={submitForm}
                                    onKeyDown={(e: KeyboardEvent) =>
                                      e.key === 'Enter' && textInputRef.current?.blur()
                                    }
                                    notInBox
                                    hasError={meta.error}
                                  />
                                )}
                              </Field>
                            </FieldRow>
                            <FieldRow>
                              <FieldLabel
                                htmlFor={`inputOption-value-${option.key}`}
                                element="label"
                              >
                                Value
                              </FieldLabel>
                              <Field name={`inputOptions[${index}].value`}>
                                {({ field }: FieldAttributes<any>) => (
                                  <FieldInput
                                    {...field}
                                    ref={valueInputRef}
                                    type="text"
                                    id={`inputOption-value-${option.key}`}
                                    value={option.value || ''}
                                    placeholder="optional"
                                    onBlur={submitForm}
                                    onKeyDown={(e: KeyboardEvent) =>
                                      e.key === 'Enter' && valueInputRef.current?.blur()
                                    }
                                    notInBox
                                  />
                                )}
                              </Field>
                            </FieldRow>
                          </FieldsContainer>
                          <Tooltip placement="top" offset="xxs" content="Delete option">
                            <ButtonIcon
                              icon={TrashIcon}
                              color="solid"
                              size="xsmall"
                              onClick={() => {
                                remove(index);
                                submitForm();
                              }}
                              radius="small"
                              ml="xxs"
                            />
                          </Tooltip>
                        </DraggableValueCard>
                      ))}
                    {Object.keys(errors).length > 0 && (
                      <ErrorMessage>
                        <AlertDangerIcon size="s" color="Danger" />
                        <Feedback ml="xxs" color="danger">
                          {typeof errors.inputOptions === 'object' &&
                            `${newOptionName} text is required`}
                        </Feedback>
                      </ErrorMessage>
                    )}
                    <AddOptionWrapper
                      onClick={() => push({ label: newOptionName, key: nanoid(3) })}
                      type="submit"
                    >
                      <AddOptionIcon color="OnyxGray" />
                      <AddOptionLabel element="span">Add option</AddOptionLabel>
                    </AddOptionWrapper>
                  </Form>
                )}
              </FieldArray>
            );
          }}
        </Formik>
      ),
      [initialValues, validationSchema, handleSubmit],
    );

    return <ValueGroup label="Options">{DraggableOptions}</ValueGroup>;
  },
);
